<template>
  <Transition name="fade">
    <div v-if="isOpen" class="fixed inset-0 flex items-center justify-center p-4 backdrop-blur-md bg-black/30 z-50" @click.self="close">
      <div class="flex flex-col bg-white rounded-lg shadow-lg p-5 sm:p-6 gap-6 w-full max-w-2xl max-h-[90dvh] overflow-auto" data-testid="estate-supply-modal">
        <header class="flex flex-col gap-2 text-center">
          <h2 class="text-xl font-semibold text-[#385660]">
            {{ $t(`property.estateSupplies.modal.${supply ? 'editTitle' : 'newTitle'}.${form.type}`) }}
          </h2>
          <p class="text-sm text-gray-500 break-words">
            {{ property?.address }}<span v-if="property?.municipality"> · {{ property.municipality }}</span><span v-if="property?.province"> ({{ property.province }})</span>
          </p>
        </header>

        <div class="supply-fields grid sm:grid-cols-2 grid-cols-1 items-start gap-x-4 gap-y-5">
          <inputPrimary class="supply-field" v-model="form.cups" :label="$t('property.estateSupplies.fields.cups')"
            :placeholder="$t('property.estateSupplies.fields.cupsPlaceholder')" :error="errors.cups" />
          <SelectPrimary class="supply-field supply-rate" v-model="form.rate" :name="`estate-supply-rate-${form.type}`" :options="rateOptions" clearable
            :label="$t(`property.estateSupplies.fields.rate.${form.type}`)"
            :placeholder="$t(`property.estateSupplies.fields.ratePlaceholder.${form.type}`)" :error="errors.rate" />
          <inputPrimary class="supply-field" v-model="form.address" :label="$t('property.estateSupplies.fields.address')"
            :placeholder="$t('property.estateSupplies.fields.addressPlaceholder')" :error="errors.address" />
          <inputPrimary class="supply-field" v-model="form.postal_code" :label="$t('property.estateSupplies.fields.postalCode')"
            :placeholder="$t('property.estateForm.fields.postalCodePlaceholder')" :error="errors.postal_code" />
        </div>

        <!-- Potencias contratadas: solo existen en la luz -->
        <div v-if="form.type === 'light'" data-testid="estate-supply-powers">
          <p class="text-[#385660] mb-2">{{ $t('property.estateForm.fields.powers') }}</p>
          <div class="grid grid-cols-3 md:grid-cols-6 gap-3">
            <div v-for="period in POWER_PERIODS" :key="period">
              <label class="block text-xs mb-1 text-[#385660]" :for="`estate-supply-${period}`">{{ period.toUpperCase() }}</label>
              <input :id="`estate-supply-${period}`" v-model="form[period]" type="text" inputmode="decimal" placeholder="0"
                class="w-full h-10 px-3 rounded-lg border border-[#3A5762] text-[#7CADB9] outline-none shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]"
                :class="{ 'border-red-500': errors[period] }" />
              <span v-if="errors[period]" class="text-red-500 text-xs">{{ errors[period] }}</span>
            </div>
          </div>
        </div>
        <p v-else class="text-xs text-gray-500">{{ $t('property.estateForm.fields.powersDisabled') }}</p>

        <p v-if="error" class="text-red-500 text-sm">{{ error }}</p>

        <div class="flex flex-col sm:flex-row justify-end gap-3">
          <ButtonWhite class="sm:w-40 w-full" @click="close" :disabled="submitting">{{ $t('property.estateForm.buttons.cancel') }}</ButtonWhite>
          <ButtonWhite class="sm:w-40 w-full" bgColor="bg-[#25B0D7]" textColor="text-white" hoverBgColor="bg-[#2AB4D9]"
            :disabled="submitting" :class="{ 'opacity-50 cursor-not-allowed': submitting }" @click="submit">
            {{ supply ? $t('property.form.buttons.saveChanges') : $t('property.form.buttons.save') }}
          </ButtonWhite>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
/**
 * Añadir un suministro de luz o gas desde la ficha de una comunidad o de un
 * inmueble, y editarlo cuando la API lo permite: el inmueble lo edita su
 * titular; los CUPS de una comunidad solo los edita ADS (para el cliente son
 * de consulta y la API responde 403).
 */
import { ref, reactive, computed, watch } from 'vue';
import axios from 'axios';
import { useI18n } from 'vue-i18n';
import ButtonWhite from '@/components/button/ButtonWhite.vue';
import inputPrimary from '@/components/inputs/input-primary.vue';
import SelectPrimary from '@/components/select/SelectPrimary.vue';
import { POWER_PERIODS } from '@/constants/supplyRates';
import { estateSupplyForm, estateSupplyRateOptions, validateEstateSupply, estateSupplyPayload } from '@/services/estateSupplies';
import { normalizeContractCups } from '@/services/contractCups';

const props = defineProps({
  isOpen: { type: Boolean, default: false },
  property: { type: Object, default: null },
  type: { type: String, default: 'light', validator: (value) => ['light', 'gas'].includes(value) },
  supply: { type: Object, default: null },
});
const emit = defineEmits(['close', 'saved']);
const { t } = useI18n();

const form = reactive(estateSupplyForm(props.supply, props.property, props.type));
const errors = ref({});
const error = ref('');
const submitting = ref(false);

// Cada apertura parte del suministro a editar o de un alta limpia de esa energía.
watch(() => [props.isOpen, props.type, props.supply], () => {
  if (!props.isOpen) return;
  Object.assign(form, estateSupplyForm(props.supply, props.property, props.type));
  errors.value = {};
  error.value = '';
});

const rateOptions = computed(() => estateSupplyRateOptions(form.type, form.rate));

const close = () => { if (!submitting.value) emit('close'); };
const cupsKey = (value) => String(value ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();

// Comprobación anticipada para que el cliente no llegue a crear un segundo
// suministro con un CUPS que ya está en su inventario. La API repite la
// validación globalmente para cubrir otros titulares y posibles carreras.
const cupsAlreadyExists = async (headers) => {
  if (props.supply) return false;
  const cups = normalizeContractCups(form.cups);
  if (!cups) return false;

  try {
    const response = await axios.get(`${import.meta.env.VITE_API_URL}/supply/user/search`, {
      headers,
      params: { query: cups, search_by: 'cups', type: 'both', per_page: 100 },
    });
    const supplies = response.data?.value?.data;
    return Array.isArray(supplies) && supplies.some((candidate) => cupsKey(candidate?.cups) === cupsKey(cups));
  } catch {
    // Si la comprobación anticipada no responde, el alta sigue y la API aplica
    // la misma restricción de forma autoritativa.
    return false;
  }
};

const submit = async () => {
  errors.value = Object.fromEntries(Object.entries(validateEstateSupply(form, {
    requireCups: !props.supply,
    requireAddress: !props.supply,
    requirePostalCode: !props.supply,
  })).map(([field, key]) => [field, t(key)]));
  error.value = '';
  if (Object.keys(errors.value).length) { error.value = t('property.estateForm.validation.review'); return; }

  submitting.value = true;
  try {
    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}`, Accept: 'application/json' };
    if (await cupsAlreadyExists(headers)) {
      errors.value.cups = t('property.estateSupplies.validation.cupsInUse');
      error.value = t('property.estateForm.validation.review');
      return;
    }
    // `estate_form` activa en la API las reglas de la ficha (sin compañía ni fin de contrato, catálogo, potencias solo luz)
    const payload = { ...estateSupplyPayload(form), estate_form: true };
    const response = props.supply
      ? await axios.put(`${import.meta.env.VITE_API_URL}/supply/update/${props.supply.id}`, payload, { headers })
      : await axios.post(`${import.meta.env.VITE_API_URL}/supply/create/${props.property.id}`, payload, { headers });
    emit('saved', response.data?.value);
    emit('close');
  } catch (err) {
    if (err.response?.status === 422 && err.response.data?.errors) {
      Object.entries(err.response.data.errors).forEach(([field, messages]) => { errors.value[field] = messages[0]; });
      error.value = t('property.estateForm.validation.review');
    } else {
      error.value = err.response?.data?.value || t('property.estateSupplies.validation.saveError');
    }
  } finally {
    submitting.value = false;
  }
};
</script>

<style scoped>
/* Normaliza los componentes compartidos solo dentro de este formulario. */
.supply-fields :deep(.supply-field) {
  margin: 0;
  min-width: 0;
  width: 100%;
  max-width: none;
}

.supply-rate :deep(label) {
  margin-bottom: 8px;
}

.supply-rate :deep(select) {
  width: 100%;
  max-width: none;
  height: 42px;
  padding-inline: 12px;
  font-size: 14px;
}

.supply-field :deep(p:empty) {
  display: none;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
